import TagManager, { DataLayerArgs } from 'react-gtm-module';

// const isDev = process.env.NODE_ENV === 'development';

const dataLayerProxy = (data: DataLayerArgs) => {
  // if (isDev) {
  //   console.log('[GTM Event]', JSON.stringify(data.dataLayer, null, 2));
  // }
  TagManager.dataLayer(data);
};

export enum Event {
  GA_TRACKING = 'GA Tracking',
  MAIN_NAVIGATION = 'main_navigation',
  FOOTER_NAVIGATION = 'footer_navigation',
  VIRTUAL_PAGE_VIEW = 'virtual_page_view',
}

export const buildEventName = {
  cta: (buttonName: string) => `CTA_${buttonName}`,

  flowStart: (formName: string) => `${formName}_flow_start`,

  flowEnd: (formName: string) => `${formName}_flow_end`,

  modal: (interaction: string) => `${interaction}_modal`,

  openModal: () => 'open_modal',

  closeModal: () => 'close_modal',
};

export enum CTAType {
  SIMPLE_BUTTON = 'Simple button',
  OVERSIZED_BUTTON = 'Oversized button',
  LINK = 'Link',
  CHECKBOX = 'Checkbox',
}

export enum ModalType {
  FORM = 'Form',
  TUTORIAL = 'Tutorial',
  INFO = 'Info',
  CONFIRMATION = 'Confirmation',
}

// MAIN NAVIGATION:
// event: main_navigation
// link_text_locale
// link_text (eventLabel similar thing)
// link_url

export const sendMainNavigationEvent = (
  linkText: string,
  linkUrl?: string,
  linkTextLocale?: string,
) => {
  dataLayerProxy({
    dataLayer: {
      event: Event.MAIN_NAVIGATION,
      link_text_locale: linkTextLocale,
      link_text: linkText,
      link_url: linkUrl,
    },
  });
};

// FOOTER_NAVIGATION:
// event: footer_navigation
// link_text_locale
// link_text
// link_url

export const sendFooterNavigationEvent = (
  linkText: string,
  linkUrl: string,
  linkTextLocale?: string,
) => {
  dataLayerProxy({
    dataLayer: {
      event: Event.FOOTER_NAVIGATION,
      link_text_locale: linkTextLocale,
      link_text: linkText,
      link_url: linkUrl,
    },
  });
};

// VIRTUAL PAGE VIEW:
// event: virtual_page_view
// user_id
// flow_name (optional, often just name of the flow that modal is for)
// page_url (actual website page url "under" the modal)
// virtual_page_url (modal step url/identifier)

export const sendVirtualPageViewEvent = (
  virtualPageUrl = '',
  options?: {
    customerId?: string | number;
    flowName?: string;
    pageUrl?: string;
    modDefaultPathname?: boolean;
  },
) => {
  const modDefaultPathname = options?.modDefaultPathname ?? true;
  const currentPage = options?.pageUrl || window.location.pathname;

  dataLayerProxy({
    dataLayer: {
      event: Event.VIRTUAL_PAGE_VIEW,
      user_id: options?.customerId,
      flow_name: options?.flowName,
      page_url: currentPage,
      virtual_page_url:
        (modDefaultPathname ? currentPage : '') + virtualPageUrl,
    },
  });
};

// CTA EVENTS:
// - basically all eventCategory items that contain CTA/cta in their name
// - triggered on cta buttons/cta link clicks
// - include confirm prestation events as this is also a CTA button
// event: CTA_{name of the button}
// CTA_type: 'Simple button' OR 'Oversized button' OR 'Link'?
// page_url
// link_text_locale
// link_text
// link_url

export const sendCTAEvent = (
  buttonName: string,
  ctaType: CTAType,
  linkText: string,
  linkTextLocale: string,
  linkUrl?: string,
) => {
  dataLayerProxy({
    dataLayer: {
      event: buildEventName.cta(buttonName),
      CTA_type: ctaType,
      page_url: window.location.pathname,
      link_text_locale: linkTextLocale,
      link_text: linkText,
      link_url: linkUrl,
    },
  });
};

// MODAL EVENTS:
// - basically all interactions with modals
// - open/next page/previous page/close etc
// event: open_modal OR close_modal OR {interaction}_modal
// modal_type: Form OR Tutorial OR any other?
// modal_name
// page_url
// virtual_page_url
// link_text_locale
// link_text
// link_url

export const sendOpenModalEvent = (
  modalName: string,
  modalType: ModalType,
  virtualPageUrl?: string,
  options?: {
    pageUrl?: string;
    linkText?: string;
    linkUrl?: string;
    linkTextLocale?: string;
  },
) => {
  dataLayerProxy({
    dataLayer: {
      event: buildEventName.openModal(),
      modal_type: modalType,
      modal_name: modalName,
      page_url: options?.pageUrl || window.location.pathname,
      virtual_page_url: virtualPageUrl,
      link_text_locale: options?.linkTextLocale,
      link_text: options?.linkText,
      link_url: options?.linkUrl,
    },
  });
};

export const sendCloseModalEvent = (
  modalName: string,
  modalType?: ModalType,
  virtualPageUrl?: string,
  options?: {
    pageUrl?: string;
    linkText?: string;
    linkUrl?: string;
    linkTextLocale?: string;
  },
) => {
  dataLayerProxy({
    dataLayer: {
      event: buildEventName.closeModal(),
      modal_type: modalType,
      modal_name: modalName,
      page_url: options?.pageUrl || window.location.pathname,
      virtual_page_url: virtualPageUrl,
      link_text_locale: options?.linkTextLocale,
      link_text: options?.linkText,
      link_url: options?.linkUrl,
    },
  });
};

export const sendModalInteractionEvent = (
  interaction: string,
  modalName: string,
  modalType: ModalType,
  virtualPageUrl?: string,
  options?: {
    pageUrl?: string;
    linkText?: string;
    linkUrl?: string;
    linkTextLocale?: string;
  },
) => {
  dataLayerProxy({
    dataLayer: {
      event: buildEventName.modal(interaction),
      modal_type: modalType,
      modal_name: modalName,
      page_url: options?.pageUrl || window.location.pathname,
      virtual_page_url: virtualPageUrl,
      link_text_locale: options?.linkTextLocale,
      link_text: options?.linkText,
      link_url: options?.linkUrl,
    },
  });
};

// FLOW START:
// - any time user starts a form
// - on any of the form fields filling
// event: {form name}_flow_start
// flow_name (often same as modal name)
// page_url
// virtual_page_url

export const sendFlowStartEvent = (
  formName: string,
  virtualPageUrl: string,
  options?: {
    flowName?: string;
    pageUrl?: string;
  },
) => {
  dataLayerProxy({
    dataLayer: {
      event: buildEventName.flowStart(formName),
      flow_name: options?.flowName || formName,
      page_url: options?.pageUrl || window.location.pathname,
      virtual_page_url: virtualPageUrl,
    },
  });
};

// FLOW END:
// - any time user submits a form
// - only on success submits, not if user is missing some fields for example
// event: {form name}_flow_end
// {any other param for data insights} (for example number_of_service_vouchers)
// flow_name (often same as modal name)
// page_url
// virtual_page_url

export const sendFlowEndEvent = (
  formName: string,
  virtualPageUrl: string,
  options?: {
    flowName?: string;
    pageUrl?: string;
    // for data insights like number_of_service_vouchers
    customData?: Record<string, any>;
  },
) => {
  dataLayerProxy({
    dataLayer: {
      event: buildEventName.flowEnd(formName),
      flow_name: options?.flowName || formName,
      page_url: options?.pageUrl || window.location.pathname,
      virtual_page_url: virtualPageUrl,
      ...options?.customData,
    },
  });
};

export enum EventCategory {
  MAIN_NAVIGATION = 'main navigation',
  BREADCRUMB = 'breadcrumb',
  FOOTER = 'footer',
  CTA = 'CTA',
  POP_UP_CLOSE = 'pop up close',
  MODAL_INTERACTION = 'modal interaction',
  OUTBOUND_TRAFFIC = 'outbound traffic',
  CONTACT = 'contact',
  CONTACT_FORM = 'contact form',
  REGISTER = 'subscription',
  DOWNLOAD = 'download',
  DASHBOARD = 'dashboard',
  INFO_BUBBLE = 'info bubble',
  ORDER_VOUCHERS_CTA = 'order vouchers CTA',
  ORDER_VOUCHERS_ONLINE = 'order request online',
  ORDER_VOUCHERS_OFFLINE = 'order request offline',
  REFUND_REQUEST_CTA = 'refund request CTA',
  EXCHANGE_REQUEST_CTA = 'exchange request CTA',
  REFUND_REQUEST = 'refund request',
  EXCHANGE = 'exchange request',
  PLAN_LEAVE = 'plan leave',
  CONFIRM_PRESTATION = 'confirm prestation',
  CONTEST_PRESTATION = 'contest prestation',
}

export enum EventAction {
  CLICK = 'click',
  BACK = 'click back',
  DELETE = 'delete',
  SUBMIT = 'submit',
  CREATION = 'creation',
  OPEN_MODAL = 'open modal',
  CLOSE_MODAL = 'close modal',
  CLOSE_MODAL_BACK_ORDER = 'close modal & back to order screen',
  GO_ORDER_DETAILS = 'go to Order details',
  GO_WORKS_MANAGEMENT = 'go to works management',
  GO_WALLET = 'go to wallet',
  NEXT = 'next',
  PREVIOUS = 'previous',
  GET_DOCUMENT = 'Get Document',
  LOG_OUT = 'Log out',
  SWITCH_USER = 'Switch user',
  ORDER_VOUCHERS = 'order vouchers',
  DOWNLOAD = 'download',
}

export enum EventLabel {
  BACK_TO_HOMEPAGE = 'back to homepage',
  ORDER_SCREEN = 'Order Screen',
  ORDER_EXCHANGE_MODAL = 'Order | Exchange modal',
  ORDER_REFUND_MODAL = 'Order | Refund modal',
  ORDER_ORDER_MODAL = 'Order | Order modal',
  ORDER_ORDER_MODAL_ORDER = 'Order modal | 1-order',
  ORDER_ORDER_MODAL_DETAILS = 'Order modal | 2-details',
  ORDER_ORDER_MODAL_PAYMENT = 'Order modal | 3-payment',
  ORDER_ORDER_MODAL_CONFIRMATION = 'Order modal | 4-confirmation',
  WALLET_SCREEN = 'Wallet Screen',
  WALLET_EXCHANGE_MODAL = 'Wallet | Exchange modal',
  WALLET_REFUND_MODAL = 'Wallet | Refund modal',
  WALLET_ORDER_MODAL = 'Wallet | Order modal',
  ELECTRONIC = 'electronic',
  PAPER = 'paper',
  EXCHANGE_CONFIRMATION = 'Exchange confirmation',
  REFUND_PAPER_LINK = 'Refund Paper link',
  NOT_ENOUGH_VOUCHER = 'not enough vouchers',
  SWITCH_FROM_PAPER = 'Switch from paper',
  SWITCH_FROM_PAPER_INFO = 'Info about switch from paper',
  WORKS_CONTEST_MODAL = 'Prestation contest modal',
  WORKS_DETAILS_MODAL = 'Prestation Details Modal',
  WORKS_FOLLOW_UP = 'Work Follow-up',
  REGISTER_COMPLETE = 'Register Complete',
  REGISTER_EID = 'Register with eID',
  REGISTER_MANUALLY = 'Register manually',
  CONDITIONS = 'Terms & Conditions',
  PRIVACY = 'Privacy',
  PROFILE_CHANGE_EMAIL_MODAL = 'Change Email Address Modal',
  PROFILE_CHANGE_PASSWORD_MODAL = 'Change Password Modal',
}

export enum EventPathname {
  ORDER_ORDER_MODAL_ORDER = '/order-modal/1-order',
  ORDER_ORDER_MODAL_DETAILS = '/order-modal/2-details',
  ORDER_ORDER_MODAL_PAYMENT = '/order-modal/3-payment',
  ORDER_ORDER_MODAL_CONFIRMATION = '/order-modal/4-confirmation',
  ONBOARDING_MODAL = '/onboarding-modal',
  REFUND_MODAL = '/refund-modal',
  EXCHANGE_MODAL = '/exchange-modal',
}

export const initAnalytics = (googleTagManagerId: string) => {
  TagManager.initialize({
    gtmId: googleTagManagerId,
  });
};

export enum DashboardActionEvent {
  ALERT = 'alert',
  CARD = 'card',
}

export const sendDashboardActionEventToGTM = (
  action: DashboardActionEvent,
  elementClicked: string,
) => {
  dataLayerProxy({
    dataLayer: {
      event: Event.GA_TRACKING,
      eventCategory: EventCategory.DASHBOARD,
      eventAction: action,
      eventLabel: elementClicked,
      eventValue: 0,
      eventNonInteraction: false,
    },
  });
};

export const sendConfirmServiceManagementEventToGTM = (
  serviceManagementType: string,
) => {
  dataLayerProxy({
    dataLayer: {
      event: Event.GA_TRACKING,
      eventCategory: EventCategory.CONFIRM_PRESTATION,
      eventAction: EventAction.CLICK,
      eventLabel: serviceManagementType,
      eventValue: 0,
      eventNonInteraction: false,
    },
  });
};

export const sendAccountSubscirptionEventToGTM = (
  eventCategory: EventCategory,
  eventAction: string,
  eventLabel: string,
) => {
  dataLayerProxy({
    dataLayer: {
      event: Event.GA_TRACKING,
      eventCategory,
      eventAction,
      eventLabel,
      eventValue: 0,
      eventNonInteraction: false,
    },
  });
};

export const sendCustomInteractionToGTM = (
  eventCategory: EventCategory,
  eventAction: string,
  eventLabel: string,
  event?: string,
  eventValue?: number,
) => {
  dataLayerProxy({
    dataLayer: {
      event: event || Event.GA_TRACKING,
      eventCategory,
      eventAction,
      eventLabel,
      eventValue: eventValue || 0,
      eventNonInteraction: false,
    },
  });
};

export const sendDownloadEventToGTM = (
  clickedLabel: string,
  downloadLink: string,
) => {
  dataLayerProxy({
    dataLayer: {
      event: Event.GA_TRACKING,
      eventCategory: EventCategory.DOWNLOAD,
      eventAction: clickedLabel,
      eventLabel: downloadLink,
      eventValue: 0,
      eventNonInteraction: false,
    },
  });
};
